/**
 * Client-side transformation of visual overrides input (from embed config) into
 * the Bach-native format expected by Answer__updateTokens / Answer__updateVisualOverrides.
 *
 * Only performs structural field-renames. Column name→GUID resolution is handled
 * server-side in prism via resolveOverrideColumnIds.
 */
import {
    ChartVizOverridesInput,
    ChartVizStyleInput,
    ConditionalFormattingInput,
    ConditionalMetricInput,
    LegendPositionOptions,
    TableVizOverridesInput,
    TableVizStyleInput,
    VisualOverridesPayloadInput,
} from '/@services/generated/graphql-types';

export interface TseFontProperties {
    color?: string;
    bold?: boolean;
    italic?: boolean;
    strikeThrough?: boolean;
    underline?: boolean;
}

export interface TseSolidBackgroundAttrs {
    color?: string;
}

export interface TseGradientBackgroundAttrs {
    backgroundFormatMidpoint?: number;
    colors?: string[];
    backgroundFormatRange?: number[];
    isAutoScaled?: boolean;
}

export interface TseConditionalFormattingRow {
    operator: string;
    value?: string;
    rangeValues?: { min: number; max: number };
    plotAsBand?: boolean;
    isHighlightRow?: boolean;
    comparisonType?: 'VALUE_BASED' | 'COLUMN_BASED' | 'PARAMETER_BASED';
    lhsColumnId?: string;
    columnToCompare?: string;
    comparisonParameterId?: string;
    fontProperties?: TseFontProperties;
    backgroundFormatType?: 'SOLID' | 'GRADIENT';
    solidBackgroundAttrs?: TseSolidBackgroundAttrs;
    gradientBackgroundAttrs?: TseGradientBackgroundAttrs;
}

export interface TseConditionalFormatting {
    rows?: TseConditionalFormattingRow[];
}

export interface TseColorPalette {
    colors?: string[];
}

export interface TseChartLegend {
    show?: boolean;
    position?: 'top' | 'bottom' | 'left' | 'right';
    colorPalette?: TseColorPalette;
}

export interface TseDataLabelFilter {
    value?: number;
    operator?: string;
}

export interface TseColumnDataLabel {
    name: string;
    visible?: boolean;
    filter?: TseDataLabelFilter;
}

export interface TseChartDataLabel {
    allLabels?: boolean;
    stackLabels?: boolean;
    columnDataLabel?: TseColumnDataLabel[];
}

export interface TseChartSummaries {
    showRowTotals?: boolean;
    showColumnTotals?: boolean;
    showRowGrandTotals?: boolean;
    showColumnGrandTotals?: boolean;
}

export interface TseGridLine {
    x?: boolean;
    y?: boolean;
}

export interface TseChartDisplay {
    summaries?: TseChartSummaries;
    regressionLine?: boolean;
    gridLine?: TseGridLine;
}

export interface TseYAxisRange {
    min?: number;
    max?: number;
}

export interface TseChartAxis {
    linkedColumns?: string[];
    showName?: boolean;
    showLabelValue?: boolean;
    yAxisRange?: TseYAxisRange;
}

export interface TseChartColumn {
    name: string;
    color?: string;
    conditionalFormatting?: TseConditionalFormatting;
}

export interface TseChartOverrides {
    legend?: TseChartLegend;
    dataLabel?: TseChartDataLabel;
    display?: TseChartDisplay;
    axis?: TseChartAxis[];
    columns?: TseChartColumn[];
    updateMaskPaths?: string[];
}

export interface TseTableColumn {
    name: string;
    wrapText?: boolean;
    show?: boolean;
    conditionalFormatting?: TseConditionalFormatting;
}

export interface TseTableDisplay {
    tableTheme?: string;
    tableContentDensity?: string;
}

export interface TseColumnSummaryVisibility {
    columnId: string;
    visible: boolean;
}

export interface TseDisplaySummaryConfig {
    showAllSummaries?: boolean;
    columnVisibility?: TseColumnSummaryVisibility[];
}

export interface TseTableOverrides {
    columns?: TseTableColumn[];
    display?: TseTableDisplay;
    displaySummaryConfig?: TseDisplaySummaryConfig;
    updateMaskPaths?: string[];
}

export interface TseVisualOverridesPayload {
    chart?: TseChartOverrides;
    table?: TseTableOverrides;
}

const LEGEND_POSITION_MAP: Record<string, LegendPositionOptions> = {
    top: LegendPositionOptions.TopLegend,
    bottom: LegendPositionOptions.BottomLegend,
    left: LegendPositionOptions.LeftLegend,
    right: LegendPositionOptions.RightLegend,
};

function transformConditionalFormattingRow(
    row: TseConditionalFormattingRow,
    defaultColumnName: string,
): ConditionalMetricInput {
    const result: Partial<ConditionalMetricInput> = {
        operator: row.operator,
        lhsColumnId: row.lhsColumnId ?? defaultColumnName,
    };

    if (row.value != null) result.value = row.value;
    if (row.rangeValues != null) result.rangeValues = row.rangeValues;
    if (row.plotAsBand != null) result.plotAsBand = row.plotAsBand;
    if (row.isHighlightRow != null) result.isHighlightRow = row.isHighlightRow;
    if (row.comparisonType != null) result.comparisonType = row.comparisonType;
    if (row.columnToCompare != null) result.rhsColumnId = row.columnToCompare;
    if (row.comparisonParameterId != null) {
        result.comparisonParameterId = row.comparisonParameterId;
    }
    if (row.fontProperties != null) result.fontProperties = row.fontProperties;
    if (row.backgroundFormatType != null) {
        result.backgroundFormatType = row.backgroundFormatType;
    }
    if (row.solidBackgroundAttrs != null) {
        result.solidBackgroundAttrs = row.solidBackgroundAttrs;
    }
    if (row.gradientBackgroundAttrs != null) {
        result.gradientBackgroundAttrs = row.gradientBackgroundAttrs;
    }

    return result as ConditionalMetricInput;
}

function transformConditionalFormatting(
    cf: TseConditionalFormatting,
    defaultColumnName: string,
): ConditionalFormattingInput {
    return {
        rows: (cf.rows ?? []).map(row =>
            transformConditionalFormattingRow(row, defaultColumnName),
        ),
    };
}

function buildChartOverrides(chart: TseChartOverrides): ChartVizStyleInput {
    const overrides: Partial<ChartVizOverridesInput> = {};

    if (chart.legend) {
        overrides.legendProperties = {
            ...(chart.legend.show != null && { showLegend: chart.legend.show }),
            ...(chart.legend.position != null && {
                position:
                    LEGEND_POSITION_MAP[chart.legend.position] ??
                    chart.legend.position,
            }),
            ...(chart.legend.colorPalette != null && {
                colorPalette: {
                    colors: (chart.legend.colorPalette.colors ?? []).filter(
                        (c): c is string => c != null,
                    ),
                },
            }),
        };
    }

    if (chart.dataLabel) {
        const { allLabels, stackLabels, columnDataLabel } = chart.dataLabel;
        overrides.dataLabelProperties = {
            ...(allLabels != null && { allLabels }),
            ...(stackLabels != null && {
                stackLabelsProperties: { isVisible: stackLabels },
            }),
            ...(columnDataLabel && {
                columnDataLabelProperties: columnDataLabel
                    .filter(
                        (col): col is NonNullable<typeof col> => col != null,
                    )
                    .map(col => ({
                        columnId: col.name,
                        properties: {
                            isVisible: col.visible,
                            filter: col.filter
                                ? {
                                      value: col.filter.value ?? 0,
                                      operator: col.filter.operator ?? '',
                                  }
                                : null,
                        },
                    })),
            }),
        };
    }

    if (chart.display) {
        const { summaries, regressionLine, gridLine } = chart.display;
        overrides.displayProperties = {
            ...(summaries && {
                summariesState: {
                    showRowTotals: summaries.showRowTotals ?? undefined,
                    showColumnTotals: summaries.showColumnTotals ?? undefined,
                    showRowGrandTotals:
                        summaries.showRowGrandTotals ?? undefined,
                    showColumnGrandTotals:
                        summaries.showColumnGrandTotals ?? undefined,
                },
            }),
            ...(regressionLine != null && {
                regressionLineProperties: { enabled: regressionLine },
            }),
            ...(gridLine && {
                gridlineProperties: {
                    xGridlineEnabled: gridLine.x ?? false,
                    yGridlineEnabled: gridLine.y ?? false,
                },
            }),
        };
    }

    if (chart.axis) {
        overrides.axisProperties = chart.axis
            .filter((ax): ax is NonNullable<typeof ax> => ax != null)
            .map(ax => {
                const linkedColumns = (ax.linkedColumns ?? []).filter(
                    (name): name is string => name != null,
                );

                return {
                    ...(linkedColumns.length && { linkedColumns }),
                    properties: {
                        ...(ax.showName != null && {
                            isNameVisible: ax.showName,
                        }),
                        ...(ax.showLabelValue != null && {
                            isLabelVisible: ax.showLabelValue,
                        }),
                        ...(ax.yAxisRange && {
                            yAxisRange: {
                                min: ax.yAxisRange.min ?? 0,
                                max: ax.yAxisRange.max ?? 0,
                            },
                        }),
                    },
                };
            });
    }

    if (chart.columns) {
        overrides.columnProperties = chart.columns
            .filter((col): col is NonNullable<typeof col> => col != null)
            .map(col => ({
                columnId: col.name,
                properties: {
                    ...(col.color != null && { color: col.color }),
                    ...(col.conditionalFormatting != null && {
                        conditionalFormatting: transformConditionalFormatting(
                            col.conditionalFormatting,
                            col.name,
                        ),
                    }),
                },
            }));
    }

    return {
        overrides,
        updateMaskPaths: (chart.updateMaskPaths ?? []).filter(
            (p): p is string => p != null,
        ),
    };
}

function buildTableOverrides(table: TseTableOverrides): TableVizStyleInput {
    const overrides: Partial<TableVizOverridesInput> = {};

    if (table.columns) {
        overrides.columnProperties = table.columns
            .filter((col): col is NonNullable<typeof col> => col != null)
            .map(col => ({
                columnId: col.name,
                columnProperty: {
                    ...(col.wrapText != null && {
                        wrapColumnText: col.wrapText,
                    }),
                    ...(col.show != null && { isHidden: !col.show }),
                    ...(col.conditionalFormatting != null && {
                        conditionalFormatting: transformConditionalFormatting(
                            col.conditionalFormatting,
                            col.name,
                        ),
                    }),
                },
            }));
    }

    if (table.display) {
        overrides.displayProperties = {
            ...(table.display.tableTheme != null && {
                tableTheme: table.display.tableTheme,
            }),
            ...(table.display.tableContentDensity != null && {
                tableContentDensity: table.display.tableContentDensity,
            }),
        };
    }

    if (table.displaySummaryConfig) {
        overrides.displaySummaryConfig = {
            ...(table.displaySummaryConfig.showAllSummaries != null && {
                showAllSummaries: table.displaySummaryConfig.showAllSummaries,
            }),
            ...(table.displaySummaryConfig.columnVisibility && {
                columnVisibility: table.displaySummaryConfig.columnVisibility
                    .filter((v): v is NonNullable<typeof v> => v != null)
                    .map(v => ({ columnId: v.columnId, visible: v.visible })),
            }),
        };
    }

    return {
        overrides,
        updateMaskPaths: (table.updateMaskPaths ?? []).filter(
            (p): p is string => p != null,
        ),
    };
}

/**
 * Transforms the embed-config visual overrides input into the Bach-native payload
 * expected by the Answer__updateTokens resolver.
 *
 * @param input - Raw visualOverridesParams from embed config
 */
export function buildVisualOverridesPayload(
    input: TseVisualOverridesPayload,
): VisualOverridesPayloadInput {
    const result: Partial<VisualOverridesPayloadInput> = {};

    if (input.chart) result.chart = buildChartOverrides(input.chart);
    if (input.table) result.table = buildTableOverrides(input.table);

    return result;
}
